import React, { useEffect, useState } from "react";
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
  Chip,
  Button,
  Modal,
  Paper,
  Tabs,
  Tab,
  Skeleton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { OrderViewData } from "../../services/order/orderHistoryService";
import { CartType } from "../../models/CartType";
import { ProfileType } from "../../models/profileType";
import { FullMenuPageData } from "../../models/fullMenuPageData";
import { MenuItemDetails } from "../../shared/menuItemDetails";
import { CartService } from "../../services/functions/cartService";
import { OrderDecisionRequestType } from "../../models/CartServiceType";

type RestaurantOrderHistoryPageProps = {
  restaurantId?: string | null;
  pendingOrders: OrderViewData[];
  collectedOrders: OrderViewData[];
  menuData: FullMenuPageData;
  loading: boolean;
  error?: string;
  profile: ProfileType | null;
  accepRejectOrderDecision: (request: OrderDecisionRequestType) => void
};

function RestaurantOrderHistoryPage({
  pendingOrders,
  collectedOrders,
  loading,
  profile,
  menuData,
  accepRejectOrderDecision
}: RestaurantOrderHistoryPageProps) {  // Retrieve the restaurant id from the route parameters
  const navigate = useNavigate();

  // Local state for orders, item labels and loading status
  const [tabIndex, setTabIndex] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  // Function to choose a Chip color based on the order status
  const getStatusChipColor = (status: String) => {
    switch (status) {
      case "PAYMENT_COMPLETE":
        return "#A6C48A";
      case "RESTAURANT_ACCEPTED_ORDER":
        return "#A6C48A";
      case "PREPARING_ORDER":
        return "#A6C48A";
      case "READY_FOR_COLLECTION":
        return "#A6C48A";
      case "COLLECTED":
        return "#162C1F";
      case "PENDING_RESTAURANT":
        return "#f21124"
      case "PARTIAL_REFUND_COMPLETE":
        return "#162C1F";
      case "FULL_REFUND_COMPLETE":
        return "#162C1F";
      case "FULL_REFUND_PROCESSING":
        return "#FFA07A";
      case "PARTIAL_REFUND_PROCESSING":
        return "#FFA07A";
      case "FAILED":
        return "FF6F61";
      default:
        return "default";
    }
  };

  const getStatusMessage = (status: String) => {
    switch (status) {
      case "PAYMENT_COMPLETE":
        return "Payment Complete";
      case "RESTAURANT_ACCEPTED_ORDER":
        return "Waiting for the restaurant";
      case "PAYMENT_PENDING":
        return "Payment Pending";
      case "PREPARING_ORDER":
        return "Preparing Order";
      case "READY_FOR_COLLECTION":
        return "Ready for Collection";
      case "COLLECTED":
        return "Collected";
      case "PENDING_RESTAURANT":
        return "Pending Refund"
      case "PARTIAL_REFUND_COMPLETE":
      case "FULL_REFUND_COMPLETE":
        return "Refund Complete";
      case "REFUND_REJECTED":
        return "Refund Rejected";
      case "FULL_REFUND_PROCESSING":
        return "Full Refund Processing";
      case "PARTIAL_REFUND_PROCESSING":
        return "Partial Refund Processing";
      case "FAILED":
        return "Failed";
      default:
        return "Unknown Status";
    }
  };


  const formatTime = (t: any): string => {
    let date: Date | null = null;

    if (!t) return "";

    // Firestore Timestamp (has toDate())
    if (typeof t.toDate === "function") {
      date = t.toDate();
    }
    // Already a Date object
    else if (t instanceof Date) {
      date = t;
    }
    // ISO string or timestamp number
    else if (typeof t === "string" || typeof t === "number") {
      const parsed = new Date(t);
      if (!isNaN(parsed.getTime())) {
        date = parsed;
      }
    }

    return date ? format(date, "yyyy-MM-dd HH:mm") : "";
  };

  function getOrderTimeInfo(cart: CartType) {
    const { orderCollected, refundedAmount, updatedAt, items } = cart;
    // When not yet collected, use the first item's endDate as the deadline
    const endDate = cart.collectBy;

    if (orderCollected) {
      if (refundedAmount !== 0) {
        return { orderTimeLabel: "Refunded at time", orderTime: updatedAt };
      }
      return { orderTimeLabel: "Collected at time", orderTime: updatedAt };
    } else {
      if (refundedAmount !== 0) {
        return { orderTimeLabel: "Refunded at time", orderTime: endDate };
      }
      return { orderTimeLabel: "Must collect by time", orderTime: endDate };
    }
  }

  const OrderCardView = (order: OrderViewData) => {
    console.log(order.cart.id, order.cart.status, getStatusMessage(order.cart.status))

    const { orderTimeLabel, orderTime } = getOrderTimeInfo(order.cart);
    return <React.Fragment key={order.cart.transactionId}>
      <Paper
        elevation={4}
        sx={{
          bgcolor: 'background.paper',
          p: 2,
          mb: 2,
          borderRadius: 2,
        }}
      >
        <ListItem
          alignItems="flex-start"
          disableGutters
          sx={{
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
          }}
        >
          <ListItemText
            primary={
              <Typography variant="h5" color="textSecondary">
                {`Order #${order.cart.id}`}
              </Typography>
            }
            secondary={
              <>
                <Typography variant="h6" color="textSecondary">
                  Restaurant price: R{((order.cart.totalAmount - order.cart.totalFees) / 100).toFixed(2)}
                </Typography>

                <Typography variant="body2" color="textSecondary">
                  Cost to customer: R{((order.cart.totalAmount) / 100).toFixed(2)}
                </Typography>

                {order.cart.refundedAmount !== 0 && (
                  <Typography variant="body2" color="textSecondary">
                    Total Refund: R{(order.cart.refundedAmount / 100).toFixed(2)}
                  </Typography>
                )}

                {order.cart.createdAt &&
                  <Typography variant="body2" color="textSecondary">
                    Ordered at time: {formatTime(order.cart.createdAt)}
                  </Typography>
                }
                {

                  <Typography variant="body2" color="textSecondary">
                    {orderTimeLabel}: {formatTime(orderTime)}
                  </Typography>

                }
                {order.cart.ecoeatsFee &&
                  <Typography variant="body2" color="textSecondary">
                    {`Total Fee: R${Math.round(order.cart.totalFees) / 100}`}
                  </Typography>
                }
                {/* <Typography variant="body2" color="textSecondary">
                  Items: {order.cart.items.map((item) => "lets put a name here. ")}
                </Typography> */}
                {order.cart.items.map(item =>
                  <Box
                    display="flex"
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    width="100%"
                    gap={2}
                  >
                    <MenuItemDetails item={item.menuCartItem} menuData={menuData} />
                  </Box>
                )}
                {order.cart.isRefundable &&
                  (profile != null && (profile.profileType === "owner" || profile.profileType === "admin"))
                  && (
                    <>
                      {order.cart.status == "PAYMENT_COMPLETE" &&
                        <Button
                          variant="outlined"
                          sx={{
                            mt: 1,
                            border: "1px solid #ffffffff",
                            borderRadius: "24px",
                            backgroundColor: "#000000ff",
                            marginRight: "16px"
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            accepRejectOrderDecision({ orderDecision: "APPROVE" });
                          }}
                        >
                          <Typography variant="button" component="h1" sx={{ color: "#ffffffff" }}>
                            Accept Order
                          </Typography>
                        </Button>
                      }
                      <Button
                        variant="outlined"
                        sx={{
                          mt: 1,
                          border: "1px solid #162C1F",
                          borderRadius: "24px",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (order.cart.status == "PAYMENT_COMPLETE") {
                            accepRejectOrderDecision({ orderDecision: "REJECT" });
                          } else {
                            navigate(`/maintainRestaurant/refundOrderInStore/${order.cart.id}`);
                          }
                        }}
                      >
                        <Typography variant="button" component="h1" sx={{ color: "#162C1F" }}>
                          {
                            order.cart.orderCollected ?
                              "Refund Order" :
                              order.cart.status == "PAYMENT_COMPLETE" ?
                                "Reject Order" :
                                "Cancel Order"
                          }
                        </Typography>
                      </Button>
                    </>
                  )}

                {order.cart.refundRequestId !== undefined &&
                  (profile != null && (profile.profileType === "owner" || profile.profileType === "admin"))
                  && (
                    <Button
                      variant="outlined"
                      sx={{
                        mt: 1,
                        border: "1px solid #162C1F",
                        borderRadius: "24px",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/refund/management/${order.cart.refundRequestId}`);
                      }}
                    >
                      <Typography variant="button" component="h1" sx={{ color: "#162C1F" }}>
                        View Refund
                      </Typography>
                    </Button>
                  )}
              </>
            }
          />
          <Chip
            label={getStatusMessage(order.cart.status)}
            sx={{
              color: "#fff",
              backgroundColor: getStatusChipColor(order.cart.status),
              mt: { xs: 2, sm: 0 }, // Add margin-top on mobile
              ml: { xs: 0, sm: 2 }, // Margin left on desktop
            }}
          />
        </ListItem>
      </Paper>
    </React.Fragment>

  }

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        {[...Array(15)].map((_, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              padding: 2,
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            {/* Center: order title and subtitle */}
            <Box sx={{ flex: 1, marginLeft: 20 }}>
              <Skeleton variant="text" width="60%" height={80} />
            </Box>

            {/* Right side: price or status */}
            <Skeleton variant="text" width={80} height={50} />
          </Box>
        ))}
      </Box>
    );
  }

  if (!pendingOrders || !collectedOrders || (pendingOrders.length + collectedOrders.length) === 0) {
    return (
      <Box
        className="patlite"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <Typography variant="h5" gutterBottom>
          No Orders Found
        </Typography>
        <Typography variant="body1" color="textSecondary">
          There are no orders for this restaurant yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3 }} className="patlite">
      <Tabs value={tabIndex} onChange={handleTabChange} centered
        sx={{
          "& .MuiTab-root": {
            color: "#757575",
            transition: "background-color 0.3s",
          },
          "& .Mui-selected": {
            color: "#162C1F !important",
            fontWeight: "bold",
          },
          "& .MuiTab-root:hover": {
            backgroundColor: "#E6F0E8",
          },
          "& .MuiTabs-indicator": {
            backgroundColor: "#162C1F",
          },
        }}
      >
        <Tab label="Pending Orders" />
        <Tab label="Accepted Orders" />
        <Tab label="Processed Orders" />
      </Tabs>
      {tabIndex === 2 && (
        <Box sx={{ mt: 2 }}>
          <List>
            {collectedOrders.map((order: OrderViewData) => (
              <OrderCardView key={order.cart.transactionId} {...order} />
            ))}
          </List>
        </Box>
      )}
      {tabIndex === 1 && (
        <Box sx={{ mt: 2 }}>
          <List>
            {pendingOrders
              .filter(po => po.cart.status == "RESTAURANT_ACCEPTED_ORDER")
              .map((order: OrderViewData) => (
                <OrderCardView key={order.cart.transactionId} {...order} />
              ))}
          </List>
        </Box>
      )}
      {tabIndex === 0 && (
        <Box sx={{ mt: 2 }}>
          <List>
            {pendingOrders
              .filter(po => po.cart.status == "PAYMENT_COMPLETE")
              .map((order: OrderViewData) => (
                <OrderCardView key={order.cart.transactionId} {...order} />
              ))}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default RestaurantOrderHistoryPage;
